import { and, asc, eq, isNull, like } from 'drizzle-orm';
import {
  installments,
  ledgerEntries,
  payees,
  paymentDues,
  payments,
  paymentsSettings,
} from '@tahi/db';
import {
  DEFAULT_PAYMENTS_SETTINGS,
  isValidRemindDays,
  paymentsSettingsId,
  type PaymentsSettings,
  type DueRowLike,
  type InstallmentLike,
  type LedgerEntryLike,
  type LedgerKind,
  type PayeeLike,
  type PaymentLike,
  type PaymentsSnapshot,
} from '@tahi/core';
import type { LocalDb } from '../localDb';

/** Device tables Payments reads (screens and the Budget commitments provider re-read on change). */
export const PAYMENTS_TABLES = [
  'payees',
  'payment_dues',
  'payments',
  'installments',
  'ledger_entries',
] as const;

type PayeeRow = typeof payees.$inferSelect;

/** v0.7.4: the Payments settings singleton, or the defaults before the first write. */
export async function getPaymentsSettings(db: LocalDb, userId: string): Promise<PaymentsSettings> {
  const r = await db
    .select()
    .from(paymentsSettings)
    .where(
      and(eq(paymentsSettings.userId, userId), eq(paymentsSettings.id, paymentsSettingsId(userId))),
    )
    .get();
  if (!r || r.deletedAt) return DEFAULT_PAYMENTS_SETTINGS;
  return {
    defaultAccountId: r.defaultAccountId ?? null,
    remindDays: isValidRemindDays(r.remindDays)
      ? r.remindDays
      : DEFAULT_PAYMENTS_SETTINGS.remindDays,
  };
}

function json<T>(v: unknown, fallback: T): T {
  if (v == null) return fallback;
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as T;
    } catch {
      return fallback;
    }
  }
  return v as T;
}

function toPayee(r: PayeeRow): PayeeLike {
  return {
    id: r.id,
    kind: r.kind,
    name: r.name,
    categoryId: r.categoryId ?? null,
    fundId: r.fundId ?? null,
    accountId: r.accountId ?? null,
    amountMinor: r.amountMinor ?? null,
    repeat: r.repeat,
    everyN: r.everyN ?? 1,
    dueDays: json<number[]>(r.dueDays, []),
    startDate: r.startDate,
    endDate: r.endDate ?? null,
    paymentCount: r.paymentCount ?? null,
    trackFrom: r.trackFrom,
    autoPay: Boolean(r.autoPay),
    remindDays: r.remindDays ?? 3,
    issuer: r.issuer ?? null,
    last4: r.last4 ?? null,
    statementDay: r.statementDay ?? null,
    dueDaysAfterStatement: r.dueDaysAfterStatement ?? null,
    creditLimitMinor: r.creditLimitMinor ?? null,
    openingBalanceMinor: r.openingBalanceMinor ?? null,
    cardholder: r.cardholder ?? null,
    lender: r.lender ?? null,
    loanType: r.loanType ?? null,
    principalMinor: r.principalMinor ?? null,
    interestBp: r.interestBp ?? null,
    note: r.note ?? null,
    deletedAt: r.deletedAt ?? null,
  };
}

export async function listPayees(
  db: LocalDb,
  userId: string,
  opts: { includeDeleted?: boolean } = {},
): Promise<PayeeLike[]> {
  const rows = await db
    .select()
    .from(payees)
    .where(
      opts.includeDeleted
        ? eq(payees.userId, userId)
        : and(eq(payees.userId, userId), isNull(payees.deletedAt)),
    )
    .orderBy(asc(payees.name));
  return rows.map(toPayee);
}

export async function getPayee(db: LocalDb, userId: string, id: string): Promise<PayeeLike | null> {
  const rows = await db
    .select()
    .from(payees)
    .where(and(eq(payees.userId, userId), eq(payees.id, id)))
    .limit(1);
  return rows[0] ? toPayee(rows[0]) : null;
}

export async function listInstallments(
  db: LocalDb,
  userId: string,
  opts: { includeDeleted?: boolean } = {},
): Promise<InstallmentLike[]> {
  const rows = await db
    .select()
    .from(installments)
    .where(
      opts.includeDeleted
        ? eq(installments.userId, userId)
        : and(eq(installments.userId, userId), isNull(installments.deletedAt)),
    )
    .orderBy(asc(installments.startMonth));
  return rows.map((r) => ({
    id: r.id,
    cardId: r.cardId,
    item: r.item,
    monthlyMinor: r.monthlyMinor,
    months: r.months,
    startMonth: r.startMonth,
    categoryId: r.categoryId ?? null,
    note: r.note ?? null,
    deletedAt: r.deletedAt ?? null,
  }));
}

/** Everything the Payments projections read, in one go. */
export async function loadPaymentsSnapshot(db: LocalDb, userId: string): Promise<PaymentsSnapshot> {
  const dueRows = await db
    .select()
    .from(paymentDues)
    .where(and(eq(paymentDues.userId, userId), isNull(paymentDues.deletedAt)));
  const paymentRows = await db
    .select()
    .from(payments)
    .where(and(eq(payments.userId, userId), isNull(payments.deletedAt)));
  const cardRows = await db
    .select()
    .from(ledgerEntries)
    .where(
      and(
        eq(ledgerEntries.userId, userId),
        isNull(ledgerEntries.deletedAt),
        like(ledgerEntries.method, 'card:%'),
      ),
    );
  const dues: DueRowLike[] = dueRows.map((r) => ({
    id: r.id,
    payeeId: r.payeeId,
    period: r.period,
    amountMinor: r.amountMinor ?? null,
    minimumMinor: r.minimumMinor ?? null,
    status: r.status,
    deletedAt: r.deletedAt ?? null,
  }));
  const pays: PaymentLike[] = paymentRows.map((r) => ({
    id: r.id,
    payeeId: r.payeeId,
    dueId: r.dueId ?? null,
    period: r.period ?? null,
    paidDate: r.paidDate,
    amountMinor: r.amountMinor,
    ledgerEntryId: r.ledgerEntryId ?? null,
    note: r.note ?? null,
    deletedAt: r.deletedAt ?? null,
  }));
  const cardEntries: LedgerEntryLike[] = cardRows.map((r) => ({
    id: r.id,
    date: r.date,
    amountMinor: r.amountMinor,
    kind: r.kind as LedgerKind,
    categoryId: r.categoryId ?? null,
    method: r.method ?? null,
    sourceModule: r.sourceModule,
    note: r.note ?? null,
    deletedAt: r.deletedAt ?? null,
  }));
  return {
    userId,
    payees: await listPayees(db, userId),
    dues,
    payments: pays,
    installments: await listInstallments(db, userId),
    cardEntries,
  };
}

/** Ids of installment charge rows already written (live or deleted), so the catch-up skips them. */
export async function listInstallmentEntryIds(db: LocalDb, userId: string): Promise<Set<string>> {
  const rows = await db
    .select({ id: ledgerEntries.id })
    .from(ledgerEntries)
    .where(and(eq(ledgerEntries.userId, userId), like(ledgerEntries.id, `${userId}:inst.%`)));
  return new Set(rows.map((r) => r.id));
}
